import React, { ReactElement } from 'react'
import InfoIcon from 'images/info-icon-default.svg'
import { Text, Flex } from 'theme-ui'
import { Svg, FlexTable } from 'ui'
import FeaturesMobile from 'components/HomePage/FeaturesMobile'
import Tippy from '@tippyjs/react'
import { CONTROLDText } from 'ui/CONTROLD'
import { numberOfServices } from 'components/Plans/SelectPlan/SelectPlan'
import useShouldRenderOnClient from 'utils/useShouldRenderOnClient'

interface FeatureDescriptionWithTooltipProps {
  featureDescription: string
  tooltipContent: string | ReactElement
}

const FeatureDescriptionWithTooltip = ({
  featureDescription,
  tooltipContent,
}: FeatureDescriptionWithTooltipProps): ReactElement => {
  return (
    <>
      <Tippy content={tooltipContent} appendTo="parent" zIndex={1} theme="screen" arrow={false}>
        <Flex
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Svg
            svg={InfoIcon}
            width="1.6rem"
            height="1.6rem"
            sx={{ flexShrink: 0 }}
            descriptiveText={`More information about ${featureDescription}`}
          />
          <Text sx={{ ml: ['0.4rem', '0.8rem'] }}>{featureDescription}</Text>
        </Flex>
      </Tippy>
    </>
  )
}

const featureData = [
  ['Features', CONTROLDText, 'Pi-Hole', 'NextDNS', 'AdGuard', 'Cloudflare', '"Smart" DNS', 'VPNs'],
  [
    <FeatureDescriptionWithTooltip
      key="Supports all devices"
      featureDescription="Supports all devices"
      tooltipContent={
        <Text sx={{ whiteSpace: 'break-spaces' }}>
          Since there are no apps to install, {CONTROLDText} supports every device. One simple
          setting change is all you need.
        </Text>
      }
    />,
    true,
    true,
    true,
    true,
    true,
    false,
    false,
  ],
  [
    <FeatureDescriptionWithTooltip
      key="No apps to install or run"
      featureDescription="No apps to install or run"
      tooltipContent={
        <Text sx={{ whiteSpace: 'break-spaces' }}>
          Not everything needs to be an app, which can waste valuable computing power.{' '}
          {CONTROLDText} works without downloading or installing anything.
        </Text>
      }
    />,
    true,
    false,
    true,
    true,
    true,
    true,
    false,
  ],
  [
    <FeatureDescriptionWithTooltip
      key="Blocks ads and trackers"
      featureDescription="Blocks ads and trackers"
      tooltipContent={
        <Text sx={{ whiteSpace: 'break-spaces' }}>
          With the flip of a switch, you can stop ads and trackers invading your privacy and wasting
          your bandwidth.
        </Text>
      }
    />,
    true,
    true,
    true,
    true,
    false,
    false,
    true,
  ],
  [
    <FeatureDescriptionWithTooltip
      key="Analytics"
      featureDescription="Analytics"
      tooltipContent={
        <Text sx={{ whiteSpace: 'break-spaces' }}>
          See all kinds of insights about your browsing activity and how Control D has helped
          (opt-in).
        </Text>
      }
    />,
    true,
    true,
    true,
    true,
    false,
    false,
    false,
  ],
  [
    <FeatureDescriptionWithTooltip
      key="Blocks IP level threats"
      featureDescription="Blocks IP level threats"
      tooltipContent={
        <Text sx={{ whiteSpace: 'break-spaces' }}>
          If {CONTROLDText} detects that a domain will resolve to a malicious IP address, it will
          not resolve, protecting you from bad actors online.
        </Text>
      }
    />,
    true,
    false,
    false,
    false,
    false,
    false,
    false,
  ],
  [
    <FeatureDescriptionWithTooltip
      key="Service level blocking"
      featureDescription="Service level blocking"
      tooltipContent={
        <Text sx={{ whiteSpace: 'break-spaces' }}>
          {CONTROLDText} helps with more than just your browser activity - when you choose to block
          a service such as Facebook or TikTok, that service will be blocked everywhere, including
          apps and notifications.
        </Text>
      }
    />,
    true,
    true,
    false,
    true,
    false,
    false,
    false,
    false,
  ],
  [
    <FeatureDescriptionWithTooltip
      key="Rule scheduling"
      featureDescription="Rule scheduling"
      tooltipContent={
        <Text sx={{ whiteSpace: 'break-spaces' }}>
          With {CONTROLDText}, you can schedule sets of rules to apply between times you choose, so
          you can get the content you want, when you want it.
        </Text>
      }
    />,
    true,
    false,
    true,
    false,
    false,
    false,
    false,
  ],
  [
    <FeatureDescriptionWithTooltip
      key="Redirects blocked services"
      featureDescription="Redirects blocked services"
      tooltipContent={
        <Text sx={{ whiteSpace: 'break-spaces' }}>
          {CONTROLDText} can make you appear as if you&apos;re in another city or country
          altogether.
        </Text>
      }
    />,
    true,
    false,
    false,
    false,
    false,
    true,
    true,
  ],
  [
    <FeatureDescriptionWithTooltip
      key="Number of supported web services"
      featureDescription="Number of supported web services"
      tooltipContent={
        <Text sx={{ whiteSpace: 'break-spaces' }}>
          {CONTROLDText} offers the most pre-configured web services to block or redirect, among all
          customizable DNS servers or VPNs.
        </Text>
      }
    />,
    `${numberOfServices}`,
    '',
    '38',
    '',
    '',
    'Variable',
    '',
  ],
  [
    <FeatureDescriptionWithTooltip
      key="No-reconnect service redirection"
      featureDescription="No-reconnect service redirection"
      tooltipContent={
        <Text sx={{ whiteSpace: 'break-spaces' }}>
          Redirect content without resetting your connection and interrupting your busy day.
        </Text>
      }
    />,
    true,
    false,
    false,
    false,
    false,
    true,
    false,
  ],
  [
    <FeatureDescriptionWithTooltip
      key="Can proxy all HTTP(S) traffic"
      featureDescription="Can proxy all HTTP(S) traffic"
      tooltipContent={
        <Text sx={{ whiteSpace: 'break-spaces' }}>
          Unlike other DNS services, {CONTROLDText} has proxy capabilities for all HTTP and HTTPS
          traffic.
        </Text>
      }
    />,
    true,
    false,
    false,
    false,
    false,
    false,
    true,
  ],
  [
    <FeatureDescriptionWithTooltip
      key="Low impact on browsing speeds"
      featureDescription="Low impact on browsing speeds"
      tooltipContent={
        <Text sx={{ whiteSpace: 'break-spaces' }}>
          While a VPN could significantly slow down your browsing by adding extra steps to your
          activity, {CONTROLDText} can actually speed it up by blocking unnecessary ad and tracker
          code.
        </Text>
      }
    />,
    true,
    true,
    true,
    true,
    true,
    true,
    false,
  ],

  [
    <FeatureDescriptionWithTooltip
      key="Protect activity"
      featureDescription={`Protect activity that doesn't use DNS`}
      tooltipContent={
        <Text sx={{ whiteSpace: 'break-spaces' }}>
          Most Internet activity can be protected via DNS, but if you&apos;re looking for a more
          comprehensive protection solution, we recommend checking out a VPN like Windscribe
          instead.
        </Text>
      }
    />,
    false,
    false,
    false,
    false,
    false,
    false,
    true,
  ],
]

export default function FeaturesGrid(): ReactElement {
  const shouldRenderOnClient = useShouldRenderOnClient()

  return (
    <Flex
      data-testid="features-grid"
      sx={{
        flexDirection: 'column',
        borderRadius: '3.2rem',
        backgroundColor: 'darkItemBG50',
        boxShadow: ({ colors }): string => `0 1.6rem 2.4rem 0 ${colors?.darkItemBoxShadow}`,
        px: ['1.6rem', '4.8rem'],
        py: ['2.4rem', '4.8rem'],
        alignItems: 'center',
        flex: 1,
        maxWidth: '108rem',
        mt: ['2.4rem', '3.2rem'],
      }}
    >
      <Flex sx={{ flexDirection: 'column' }}>
        <Text
          as="h2"
          sx={{
            fontSize: ['2.8rem', '4rem'],
            fontWeight: 700,
            justifyContent: 'center',
            textAlign: ['center', 'center', 'left'],
            mb: ['1.6rem', 0],
            pl: '1.6rem',
          }}
        >
          Packed with features, there&apos;s nothing else like it.
        </Text>
        <FeaturesMobile data={featureData} />
        {shouldRenderOnClient && <FlexTable data={featureData} shouldTransposeData />}
      </Flex>
    </Flex>
  )
}
